<script setup lang="ts">
  import { AddUserDialog, UserManagementTable } from '@haskoning/vue-user-management'
  import DeleteDialog from '@/components/dialog/DeleteDialog.vue'
  import { useProjectStore } from '@/stores/project'
  import HeaderWithActions from '@/components/projectDetail/components/HeaderWithActions.vue'
  import PrimaryButton from '@/components/shared/PrimaryButton.vue'

  const store = useProjectStore()
  const projectUserStore = useProjectUserStore()
  const authorizationStore = useAuthorizationStore()

  const userManagementDrawer = ref<HTMLElement | null>(null)
  const canAdd = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: 'add_projectuser'
    })
  })

  const canEdit = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: 'change_projectuser'
    })
  })

  useResizeableElement({
    ref: userManagementDrawer,
    widthProperty: 'userManagementDrawer',
    elementGetter: vDrawerElementGetter
  })
</script>

<template>
  <v-navigation-drawer
    :temporary="true"
    :scrim="false"
    ref="userManagementDrawer"
    :model-value="store.userManagementDrawer.shown"
    :width="store.userManagementDrawer.width"
    :class="['user-drawer', 'resize-handler-gi']"
  >
    <HeaderWithActions>
      <template #default> User Management </template>
      <template #actions>
        <v-tooltip v-if="canAdd" text="Invite people to project">
          <template v-slot:activator="{ props }">
            <PrimaryButton
              v-bind="props"
              @click="projectUserStore.toggleAddUserDialog()"
              prepend-icon="mdi-plus"
            >
              Add
            </PrimaryButton>
          </template>
        </v-tooltip>
      </template>
      <template #subheader>
        <template v-if="canAdd">
          Please configure the permissions for users within this project
        </template>
        <template v-else> Manage access to the project</template>
      </template>
    </HeaderWithActions>
    <v-divider />
    <v-lazy>
      <div>
        <UserManagementTable
          :delete-dialog="DeleteDialog"
          :can-edit="!!canEdit"
          auth-role="ProjectUser"
          :auth-model-user-store="projectUserStore"
          auth-model-key="project"
        />
      </div>
    </v-lazy>
  </v-navigation-drawer>
  <AddUserDialog
    :auth-model-user-store="projectUserStore"
    :auth-model-store="useProjectStore()"
    auth-model-key="project"
  />
</template>

<style scoped>
  .resize-handler-gi {
    border-right-width: 10px;
    z-index: 1;
    box-shadow: none;
  }

  .hidden {
    display: none;
    opacity: 0;
    transition: opacity 0.5s ease;
  }

  .user-drawer {
    transition: border-bottom-color 0.3s;
    border-right: 10px solid rgba(0, 0, 0, 0.1);
  }
</style>
